import React from "react";
import isArray from "lodash/isArray";
import { InputText, Label, Dropdown } from "../../../../components/Inputs";
import {
  validateAreaCode,
  createSortByLabel,
} from "../../../../components/Utils";

const CompanyAddressForm = React.forwardRef(function CompanyAddressForm(
  {
    className = "",
    companies = [],
    model = {
      company_id: "",
      name: "",
      address: {
        street: "",
        houseNumber: "",
        areaCode: "",
        city: "",
      },
    },
    isVertical,
    namePrefix = "",
    company,
    getCompany,
    disabled,
  },
  ref,
) {
  const getState = (props) => {
    const { model } = props;
    const address = model.address || {};
    return {
      company_id: model.company_id || "",
      name: model.name || "",
      street: address.street || "",
      houseNumber: address.houseNumber || "",
      areaCode: address.areaCode || "",
      city: address.city || "",
      error: {
        company_id: null,
        name: null,
        street: null,
        houseNumber: null,
        areaCode: null,
        city: null,
      },
    };
  };

  const [state, setState] = React.useState(() => getState({ model }));

  const validate = () => {
    const { error, ...modelData } = state;
    // check if all values are set
    const newError = { ...error };
    let isValid = true;
    Object.keys(modelData).forEach((key) => {
      if (newError[key] !== undefined && modelData[key] === "") {
        newError[key] = true;
        isValid = false;
      }
    });
    if (!validateAreaCode(modelData.areaCode)) {
      isValid = false;
      newError.areaCode = true;
    }
    if (!isValid) {
      setState((prev) => ({ ...prev, error: newError }));
      return null;
    }
    const { company_id, name, ...address } = modelData;
    const newModel = { name, address };
    if (model && model.address) {
      newModel.address = { ...model.address, ...address };
    }
    // get company id
    const companyId = parseInt(company_id);
    newModel.company_id = !isNaN(companyId) ? companyId : 0;
    return newModel;
  };

  React.useImperativeHandle(ref, () => ({
    getModel: () => validate(),
  }));

  const setCompany = (company_id) => {
    let newState = { company_id };
    // company is new and not yet saved
    if (!companies || companies.length === 0) {
      const newCompany = getCompany();
      const { street, houseNumber, areaCode, city } = newCompany.address;
      newState = {
        ...newState,
        name: newCompany.name,
        street,
        houseNumber,
        areaCode,
        city,
      };
    } else {
      let found = (companies || []).find((item) => item.id === company_id);
      if (!found && company && company_id === company.id) {
        found = company;
      }
      if (found) {
        const address = isArray(found.address)
          ? found.address[0]
          : found.address;
        const { street, houseNumber, areaCode, city } = address;
        newState = {
          ...newState,
          name: found.name,
          street,
          houseNumber,
          areaCode,
          city,
        };
      }
    }
    setState((prevState) => ({
      ...newState,
      error: {
        ...prevState.error,
        company_id: null,
        name: null,
      },
    }));
  };

  const renderStreetHouse = (model, className) => {
    const { name, street, houseNumber, error } = model || {};
    return (
      <div
        className={`${className} ${
          error.street || error.houseNumber ? " error-group" : ""
        }`}
      >
        <Label
          className="display-inline-block"
          htmlFor={`${namePrefix}address_form_street`}
        >{`Straße`}</Label>
        <Label className="display-inline-block">{`/`}</Label>
        <Label
          className="display-inline-block"
          htmlFor={`${namePrefix}address_form_housenumber`}
        >{`Hausnummer *`}</Label>
        <div className="w3-row">
          <div className="w3-col s8 pad-sm-rht">
            <InputText
              id={`${namePrefix}address_form_street`}
              data-test-id={`${namePrefix}address_form_street`}
              className={`w3-block w3-border ${
                error.street ? "w3-border-red" : ""
              }`}
              value={street}
              disabled={disabled || name === ""}
              onChange={(event) => {
                const value = event.target.value;
                setState((prev) => ({ ...prev, street: value }));
              }}
            />
          </div>
          <div className="w3-col s4 pad-sm-lft">
            <InputText
              id={`${namePrefix}address_form_housenumber`}
              data-test-id={`${namePrefix}address_form_housenumber`}
              className={`w3-block w3-border ${
                error.houseNumber ? "w3-border-red" : ""
              }`}
              value={houseNumber}
              disabled={disabled || name === ""}
              onChange={(event) => {
                const value = event.target.value;
                setState((prev) => ({ ...prev, houseNumber: value }));
              }}
            />
          </div>
        </div>
      </div>
    );
  };
  const renderAreaCity = (model, className) => {
    const { name, areaCode, city, error } = model || {};
    return (
      <div
        className={`${className} ${
          error.areaCode || error.city ? " error-group" : ""
        }`}
      >
        <Label
          className="display-inline-block"
          htmlFor={`${namePrefix}area_code`}
        >{`PLZ`}</Label>
        <Label className="display-inline-block">{`/`}</Label>
        <Label
          className="display-inline-block"
          htmlFor={`${namePrefix}city`}
        >{`Ort *`}</Label>
        <div className="w3-row">
          <div className="w3-col s4 pad-sm-rht">
            <InputText
              id={`${namePrefix}area_code`}
              data-test-id={`${namePrefix}area_code`}
              className={`w3-block w3-border ${
                error.areaCode ? "w3-border-red" : ""
              }`}
              value={areaCode}
              disabled={disabled || name === ""}
              onChange={(event) => {
                const value = event.target.value;
                setState((prev) => ({ ...prev, areaCode: value }));
              }}
            />
          </div>
          <div className="w3-col s8 pad-sm-lft">
            <InputText
              id={`${namePrefix}city`}
              data-test-id={`${namePrefix}city`}
              className={`w3-block w3-border ${
                error.city ? "w3-border-red" : ""
              }`}
              value={city}
              disabled={disabled || name === ""}
              onChange={(event) => {
                const value = event.target.value;
                setState((prev) => ({ ...prev, city: value }));
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const { company_id, name, error } = state;
  const isSaved = model && (model.address.id || model.id);

  const CompanyOptions = (companies || []).map((item) => ({
    label: item.name,
    value: item.id,
  }));
  if (company) {
    CompanyOptions.push({ label: company.name, value: company.id });
    CompanyOptions.sort(createSortByLabel());
  }
  const companyMarkup = isSaved ? null : (
    <div
      className={`pad-top form-group-item ${error.company_id ? " error-group" : ""}`}
    >
      <Label htmlFor={`${namePrefix}company_id`}>{`Firma*`}</Label>
      <Dropdown
        id={`${namePrefix}company_id`}
        data-test-id={`${namePrefix}company_id`}
        className={`w3-block w3-border`}
        value={company_id}
        placeholder="Bitte auswählen"
        options={CompanyOptions}
        onChange={(event) => setCompany(event.value)}
        showClear
        disabled={disabled}
      />
      {error.company_id && (
        <div className="error-label pad-sm-top">{`Die Firma ist erforderlich`}</div>
      )}
    </div>
  );
  return (
    <div className={className}>
      {companyMarkup}
      <div
        className={`pad-top form-group-item ${error.name ? " error-group" : ""}`}
      >
        <Label htmlFor={`${namePrefix}name`}>{`Firmenname*`}</Label>
        <InputText
          id={`${namePrefix}name`}
          data-test-id={`${namePrefix}name`}
          className={`w3-block w3-border ${error.name ? "w3-border-red" : ""}`}
          value={name}
          disabled={disabled || company_id === ""}
          onChange={(event) => {
            const value = event.target.value;
            setState((prev) => ({ ...prev, name: value }));
          }}
        />
      </div>
      <div className="neg-margin-lr">
        <div className="w3-row">
          {renderStreetHouse(
            state,
            `w3-col ${isVertical ? "m12" : "m6"} pad-lr pad-top form-group-item`,
          )}
          {renderAreaCity(
            state,
            `w3-col ${isVertical ? "m12" : "m6"} pad-lr pad-top form-group-item`,
          )}
        </div>
      </div>
    </div>
  );
});

export default CompanyAddressForm;
